import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// 🔑 Utility: Inject userId into every query so RTKQ caches per-user
const withUserScope = (queryArgs) => {
  const userInfo = JSON.parse(sessionStorage.getItem("user_info")) || {};
  const userScope = userInfo.id ?? "GUEST";
  return { ...queryArgs, __userScope: userScope };
};

// 🔑 Utility: Generate user-scoped tag
const userTag = (type, arg) => {
  const scope = arg?.__userScope ?? "GUEST";
  return [{ type, id: scope }];
};

// -----------------------------------------
// Base Query
// -----------------------------------------
const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_API,
    prepareHeaders: (headers) => {
        try {
            // Retrieve tokens dynamically at request time
            const tokens = JSON.parse(sessionStorage.getItem("tokens")) || {};
            const api_key = JSON.parse(sessionStorage.getItem("api_key")) || {};
            const userInfo = JSON.parse(sessionStorage.getItem("user_info")) || {};

            if (tokens.access_token) {
                headers.set('Authorization', `Bearer ${tokens.access_token}`);
            }
            if (api_key.user_api_key) {
                headers.set('User_api_key', api_key.user_api_key);
            }
            if (userInfo.id) {
                headers.set('X-User-Id', userInfo.id);
            }
        } catch (error) {
            console.error("Error retrieving tokens:", error);
        }
        return headers;
    },
});

// -----------------------------------------
// API Slice
// -----------------------------------------
export const Api = createApi({
    reducerPath:'detection',
    baseQuery, 
    tagTypes: ['Upload', 'Results'],
    endpoints: (builder) => ({
    
        // -----------------------------------------
        // Upload Batch
        // -----------------------------------------
        uploadBatch: builder.mutation({
            query: (newBatch) => withUserScope({
                url: "/process_batch/",
                method: "POST",
                body: newBatch,
            }),
            invalidatesTags: (result, error, arg) => userTag("Results", arg),
        }),

        // -----------------------------------------
        // Fetch Batches
        // -----------------------------------------
        fetchBatches: builder.query({
            query: () => withUserScope({
                url: '/batches_overview/',
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        fetchBatchNames: builder.query({
            query: () => withUserScope({
                url: '/batch_names/',
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        fetchInfo: builder.query({
            query: () => withUserScope({
                url: '/get_feature_info/',
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        // -----------------------------------------
        // Module & Cell Info
        // -----------------------------------------
        downloadCellStats: builder.query({
            query: ({ batchName, moduleName }) => withUserScope({
                url: `/module_cell_stats/${batchName}/${moduleName}/`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        getCellInfo: builder.query({
            query: ({ batchName, moduleName, cellName }) => withUserScope({
                url: `/results/${batchName}/${moduleName}/${cellName}/`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        fetchModule: builder.query({
            query: ({ batchName, moduleName }) => withUserScope({
                url: `/results/${batchName}/${moduleName}/`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        fetchColourmaps: builder.query({
            query: ({ batchName, moduleName }) => withUserScope({
                url: `/module_colourmaps/${batchName}/${moduleName}/`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        downloadModuleStats: builder.query({
            query: (batchName) => withUserScope({
                url: `/batch_module_stats/${batchName}/`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        // -----------------------------------------
        // Batch Deletion
        // -----------------------------------------
        deleteBatch: builder.mutation({
            query: (batchName) => withUserScope({
                url: `/delete_batch/${batchName}`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, arg) => userTag("Results", arg),
        }),

        // -----------------------------------------
        // Batch & Colourmaps
        // -----------------------------------------
        fetchBatch: builder.query({
            query: (batchName) => withUserScope({
                url: `/results/${batchName}/`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        fetchBatchColourmaps: builder.query({
            query: ({ batchName, block }) => withUserScope({
                url: `/colourmaps/${batchName}/${block}/`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        // -----------------------------------------
        // Processing Status
        // -----------------------------------------
        getProcessingStatus: builder.query({
            query: (taskId) => withUserScope({
                url: `/process_batch_status/${taskId.replace(/[{}]/g, '')}`,
                method: 'GET',
            }),
            transformErrorResponse: (error) => {
                if (error.status === 404) {
                return { status: "completed", progress: 100 };
                }
                return error;
            },
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),

        getProcessBatchMessage: builder.query({
            query: (batchName) => withUserScope({
                url: `/process_batch_message/${batchName}`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => userTag("Results", arg),
        }),
    }),
});

export const {
    useUploadBatchMutation,
    useFetchBatchesQuery,
    useFetchBatchNamesQuery,
    useDownloadCellStatsQuery,
    useFetchModuleQuery,
    useFetchColourmapsQuery,
    useDownloadModuleStatsQuery,
    useFetchBatchQuery,
    useFetchBatchColourmapsQuery,
    useGetProcessingStatusQuery,
    useDeleteBatchMutation,
    useGetProcessBatchMessageQuery,
    useFetchInfoQuery,
    useGetCellInfoQuery
} = Api;